import { useEffect, useMemo, useRef, useState } from 'react';

export type UseIntersectionObserverParams = IntersectionObserverInit & {
  onIntersect?: () => void;
};
export const useIntersectionObserver = <Element extends HTMLElement>(
  params?: UseIntersectionObserverParams,
) => {
  const intersectionRef = useRef<Element>(null);

  const [isIntersecting, setIsIntersecting] = useState(false);
  const [memoedParams] = useState(params);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);

        if (entry.isIntersecting) {
          memoedParams?.onIntersect?.();
        }
      },
      {
        // how many percent of the bounding box is viewed before triggering callback
        threshold: memoedParams?.threshold ?? 0.4,
        root: memoedParams?.root,
        // how many pixel before checking intersection threshold
        rootMargin: memoedParams?.rootMargin,
      },
    );

    const countdownElm = intersectionRef.current;
    if (countdownElm) {
      observer.observe(countdownElm);
    }

    return () => {
      if (countdownElm) {
        observer.unobserve(countdownElm);
      }
    };
  }, [memoedParams]);

  return useMemo(() => {
    return {
      intersectionRef,
      isIntersecting,
    };
  }, [isIntersecting]);
};
